import React, { useEffect, useMemo, useRef, useState } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Data Infuser — "데이터 인퓨저란?" 소개 섹션 우측 다이어그램
// 원천 데이터(DB·파일·API) → Data Infuser 엔진(게이트웨이 관리) → API 출력(REST·RPC·MCP)
// 으로 데이터가 흐르는 구조를 2D 플로우로 표현. 연결선은 점선이 흐르듯 애니메이션.
// 화이트 코퍼레이트 톤(.cp) + 브랜드 레드(#b8313d). 색상은 prop 으로 교체 가능.
// 폭에 맞춰 920×560 스테이지를 scale, <720px 에서는 세로 스택으로 폴백.
// ─────────────────────────────────────────────────────────────────────────────

const STAGE_W = 920;
const STAGE_H = 560;
const BRAND_RED = '#b8313d';

// ── 색상 유틸 ────────────────────────────────────────────────────────────────
const hx = (h: string): number[] => {
  let s = h.replace('#', '');
  if (s.length === 3)
    s = s
      .split('')
      .map((c) => c + c)
      .join('');
  return [parseInt(s.slice(0, 2), 16), parseInt(s.slice(2, 4), 16), parseInt(s.slice(4, 6), 16)];
};
const toHex = (a: number[]): string =>
  '#' +
  a
    .map((v) =>
      Math.max(0, Math.min(255, Math.round(v)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('');
const mix = (c: string, t: string, amt: number): string => {
  const A = hx(c);
  const B = hx(t);
  return toHex(A.map((v, i) => v + (B[i] - v) * amt));
};

// ── 데이터 ───────────────────────────────────────────────────────────────────
type SrcType = 'db' | 'file' | 'api';
type Category = { title: string; type: SrcType; items: string[]; x: number; y: number; w: number; h: number };
type Output = { name: string; sub: string; badge: string; x: number; y: number; w: number; h: number };

const SX = 16;
const SW = 258;
const CATEGORIES: Category[] = [
  {
    title: '데이터베이스',
    type: 'db',
    items: ['PostgreSQL', 'Oracle', 'Cubrid', 'MySQL / MariaDB', 'Tibero'],
    x: SX,
    y: 90,
    w: SW,
    h: 132,
  },
  { title: '파일', type: 'file', items: ['CSV', 'Excel', 'JSON', 'XML'], x: SX, y: 250, w: SW, h: 96 },
  { title: 'API', type: 'api', items: ['REST API', 'SOAP', 'GraphQL', 'OData'], x: SX, y: 374, w: SW, h: 96 },
];

const OX = 706;
const OW = 198;
const OUTPUTS: Output[] = [
  { name: 'REST API', sub: '표준 REST 엔드포인트', badge: 'REST', x: OX, y: 136, w: OW, h: 64 },
  { name: 'RPC', sub: '원격 프로시저 호출', badge: 'RPC', x: OX, y: 248, w: OW, h: 64 },
  { name: 'MCP Server', sub: 'AI 에이전트 연동', badge: 'MCP', x: OX, y: 360, w: OW, h: 64 },
];

const FEATURES = ['보안', '로깅', '캐싱', 'Rate-Limit', '인증', 'ACL'];

const ENTRY = { x: 356, y: 280 };
const EXIT = { x: 604, y: 280 };

// ── 카테고리 아이콘 ──────────────────────────────────────────────────────────
const SourceIcon: React.FC<{ type: SrcType }> = ({ type }) => {
  if (type === 'db') {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2} aria-hidden>
        <ellipse cx={12} cy={5.5} rx={7.5} ry={3} />
        <path d="M4.5 5.5v6c0 1.66 3.36 3 7.5 3s7.5-1.34 7.5-3v-6" />
        <path d="M4.5 11.5v6c0 1.66 3.36 3 7.5 3s7.5-1.34 7.5-3v-6" />
      </svg>
    );
  }
  if (type === 'file') {
    return (
      <svg
        width="19"
        height="19"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#fff"
        strokeWidth={2}
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M6 2.5h8l4 4v15H6z" />
        <path d="M14 2.5v4h4" />
        <path d="M9 13h6M9 17h6" />
      </svg>
    );
  }
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#fff"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M8 7l-5 5 5 5" />
      <path d="M16 7l5 5-5 5" />
    </svg>
  );
};

// ── Props ────────────────────────────────────────────────────────────────────
type Props = {
  accent?: string;
  /** 엔진 중앙 로고. 프로덕션에서는 로컬 asset import 를 권장. */
  logoSrc?: string;
  animate?: boolean;
};

const DEFAULT_LOGO = 'https://avatars.githubusercontent.com/u/71760422?s=200&v=4';

const DataInfuserDiagram: React.FC<Props> = ({ accent = BRAND_RED, logoSrc = DEFAULT_LOGO, animate = true }) => {
  const c = useMemo(
    () => ({
      accent,
      light: mix(accent, '#ffffff', 0.16),
      mid: mix(accent, '#ffffff', 0.12),
      dark: mix(accent, '#1a0407', 0.34),
      line: mix(accent, '#ffffff', 0.74),
    }),
    [accent],
  );

  const paths = useMemo(() => {
    const list: { d: string; dur: number }[] = [];
    CATEGORIES.forEach((cat, i) => {
      const a = { x: cat.x + cat.w, y: cat.y + cat.h / 2 };
      list.push({
        d: `M ${a.x} ${a.y} C ${a.x + 64} ${a.y} ${ENTRY.x - 64} ${ENTRY.y} ${ENTRY.x} ${ENTRY.y}`,
        dur: 1.7 + i * 0.12,
      });
    });
    OUTPUTS.forEach((o, i) => {
      const b = { x: o.x, y: o.y + o.h / 2 };
      list.push({
        d: `M ${EXIT.x} ${EXIT.y} C ${EXIT.x + 64} ${EXIT.y} ${b.x - 64} ${b.y} ${b.x} ${b.y}`,
        dur: 1.5 + i * 0.14,
      });
    });
    return list;
  }, []);

  // 폭에 맞춰 스테이지 스케일 + 모바일 폴백 판정
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth;
      setCompact(w < 720);
      setScale(Math.min(1, w / STAGE_W));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── 모바일 폴백: 세로 스택 ──────────────────────────────────────────────────
  if (compact) {
    return (
      <div className="di-diagram di-diagram--compact" ref={wrapRef}>
        <div className="di-diagram__group-label">원천 데이터</div>
        {CATEGORIES.map((cat) => (
          <div key={cat.title} className="di-diagram__mcard">
            <span
              className="di-diagram__mchip-icon"
              style={{ background: `linear-gradient(150deg, ${c.mid}, ${c.accent})` }}
            >
              <SourceIcon type={cat.type} />
            </span>
            <div>
              <div className="di-diagram__mtitle">{cat.title}</div>
              <div className="di-diagram__mtags">
                {cat.items.map((it) => (
                  <span key={it} className="di-diagram__mtag" style={{ color: c.accent }}>
                    {it}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
        <div className="di-diagram__marrow">↓</div>
        <div
          className="di-diagram__mcore"
          style={{ background: `linear-gradient(160deg, ${c.light}, ${c.accent} 52%, ${c.dark})` }}
        >
          <div className="di-diagram__mcore-title">Data Infuser</div>
          <div className="di-diagram__mcore-sub">데이터 통합 · API 게이트웨이 엔진</div>
          <div className="di-diagram__mfeatures">
            {FEATURES.map((f) => (
              <span key={f} className="di-diagram__mfeature">
                {f}
              </span>
            ))}
          </div>
        </div>
        <div className="di-diagram__marrow">↓</div>
        <div className="di-diagram__group-label">API 출력 채널</div>
        {OUTPUTS.map((o) => (
          <div key={o.name} className="di-diagram__mcard">
            <span
              className="di-diagram__mchip-badge"
              style={{ background: `linear-gradient(150deg, ${c.mid}, ${c.accent})` }}
            >
              {o.badge}
            </span>
            <div>
              <div className="di-diagram__mtitle">{o.name}</div>
              <div className="di-diagram__msub">{o.sub}</div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ── 데스크톱: 2D 플로우 스테이지 ────────────────────────────────────────────
  const card: React.CSSProperties = {
    position: 'absolute',
    background: '#ffffff',
    border: '1px solid #f1dde0',
    borderRadius: 15,
    boxShadow: '0 6px 16px rgba(120,20,30,0.08)',
  };

  return (
    <div className="di-diagram" ref={wrapRef}>
      <div className="di-diagram__wrap" style={{ height: STAGE_H * scale }}>
        <div className="di-diagram__stage" style={{ width: STAGE_W, height: STAGE_H, transform: `scale(${scale})` }}>
          {/* 컬럼 헤더 */}
          <div style={{ position: 'absolute', left: 16, top: 30, width: 258, textAlign: 'center' }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.16em', color: c.accent }}>SOURCE</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#26272f', marginTop: 2 }}>원천 데이터</div>
          </div>
          <div style={{ position: 'absolute', left: 646, top: 30, width: 258, textAlign: 'center' }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.16em', color: c.accent }}>OUTPUT</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#26272f', marginTop: 2 }}>API 출력 채널</div>
          </div>

          {/* 연결선 */}
          <svg
            viewBox={`0 0 ${STAGE_W} ${STAGE_H}`}
            width={STAGE_W}
            height={STAGE_H}
            style={{ position: 'absolute', inset: 0, overflow: 'visible' }}
            aria-hidden
          >
            <defs>
              <filter id="diGlow" x="-40%" y="-40%" width="180%" height="180%">
                <feGaussianBlur stdDeviation="2.2" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            {paths.map((p, i) => (
              <path key={`base${i}`} d={p.d} fill="none" stroke={c.line} strokeWidth={2} strokeLinecap="round" />
            ))}
            {paths.map((p, i) => (
              <path
                key={`flow${i}`}
                d={p.d}
                fill="none"
                stroke={c.accent}
                strokeWidth={2.6}
                strokeLinecap="round"
                strokeDasharray="0.5 16"
                filter="url(#diGlow)"
                style={animate ? { animation: `di-dash ${p.dur}s linear infinite` } : undefined}
              />
            ))}
            <circle cx={ENTRY.x} cy={ENTRY.y} r={5} fill="#ffffff" stroke={c.accent} strokeWidth={2.5} />
            <circle cx={EXIT.x} cy={EXIT.y} r={5} fill="#ffffff" stroke={c.accent} strokeWidth={2.5} />
          </svg>

          {/* 원천 데이터 그룹 카드 */}
          {CATEGORIES.map((cat) => (
            <div
              key={cat.title}
              style={{
                ...card,
                left: cat.x,
                top: cat.y,
                width: cat.w,
                height: cat.h,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 9,
                padding: '12px 15px',
                borderRadius: 15,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                <span
                  style={{
                    flex: 'none',
                    width: 35,
                    height: 35,
                    borderRadius: 9,
                    background: `linear-gradient(150deg, ${c.mid}, ${c.accent})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <SourceIcon type={cat.type} />
                </span>
                <span style={{ fontSize: 16, fontWeight: 800, color: '#26272f', letterSpacing: '-0.01em' }}>
                  {cat.title}
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {cat.items.map((it) => (
                  <span
                    key={it}
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: c.accent,
                      padding: '4px 7.5px',
                      borderRadius: 999,
                      background: '#fbeef0',
                      border: '1px solid #f3d9dd',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {it}
                  </span>
                ))}
              </div>
            </div>
          ))}

          {/* Data Infuser 엔진 */}
          <div
            style={{
              position: 'absolute',
              left: 356,
              top: 120,
              width: 248,
              height: 320,
              borderRadius: 24,
              background: `linear-gradient(160deg, ${c.light} 0%, ${c.accent} 52%, ${c.dark} 100%)`,
              boxShadow: '0 22px 42px rgba(150,25,38,0.32), inset 0 0 0 1px rgba(255,255,255,0.18)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '24px 18px',
              color: '#fff',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '-18%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '60%',
                aspectRatio: '1',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255,255,255,0.35), rgba(255,255,255,0) 65%)',
              }}
            />
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div
                style={{
                  position: 'absolute',
                  width: 74,
                  height: 74,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(255,255,255,0.45), rgba(255,255,255,0) 66%)',
                  animation: animate ? 'di-pulse 2.8s ease-in-out infinite' : undefined,
                }}
              />
              <img
                src={logoSrc}
                alt="Data Infuser"
                style={{
                  position: 'relative',
                  width: 55,
                  height: 55,
                  borderRadius: 14,
                  background: '#fff',
                  boxShadow: '0 6px 16px rgba(80,0,12,0.32), inset 0 0 0 1px rgba(255,255,255,0.6)',
                }}
              />
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, marginTop: 13, letterSpacing: '-0.01em' }}>Data Infuser</div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: 'rgba(255,255,255,0.82)',
                marginTop: 4,
                textAlign: 'center',
              }}
            >
              데이터 통합 · API 게이트웨이 엔진
            </div>
            <div style={{ width: '78%', height: 1, background: 'rgba(255,255,255,0.22)', margin: '18px 0 13px' }} />
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.72)' }}>
              GATEWAY 관리 기능
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, justifyContent: 'center', marginTop: 11 }}>
              {FEATURES.map((f) => (
                <span
                  key={f}
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#fff',
                    padding: '6px 12px',
                    borderRadius: 999,
                    background: 'rgba(255,255,255,0.14)',
                    border: '1px solid rgba(255,255,255,0.3)',
                  }}
                >
                  {f}
                </span>
              ))}
            </div>
          </div>

          {/* API 출력 카드 */}
          {OUTPUTS.map((o) => (
            <div
              key={o.name}
              style={{
                ...card,
                left: o.x,
                top: o.y,
                width: o.w,
                height: o.h,
                display: 'flex',
                alignItems: 'center',
                gap: 13,
                padding: '0 14px',
                borderRadius: 15,
              }}
            >
              <span
                style={{
                  flex: 'none',
                  width: 42,
                  height: 42,
                  borderRadius: 10,
                  background: `linear-gradient(150deg, ${c.mid}, ${c.accent})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 11.5,
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                }}
              >
                {o.badge}
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#26272f', letterSpacing: '-0.01em' }}>
                  {o.name}
                </div>
                <div style={{ fontSize: 10.5, fontWeight: 500, color: '#8a7a7c', marginTop: 2, whiteSpace: 'nowrap' }}>
                  {o.sub}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DataInfuserDiagram;
